/**
 * voice-v8 — „Material einspeisen".
 *
 * Ein Eingang über das +-Menü: Texte (Notizen, KI-Dialoge, Mitschriften)
 * reinkopieren → sie werden als Projekt-Datei in einem dedizierten Projekt
 * „Eingespeistes Material" abgelegt (derselbe Speicher, den die Biografie eh
 * liest) → Deep-Rebuild zieht Themen/Ereignisse/Personen heraus.
 *
 * Kein Parallel-Speicher: nutzt ausschließlich die bestehende projects-api +
 * /api/biography/regenerate.
 */

import {
  listProjects,
  createProject,
  listProjectFiles,
  createProjectFile,
  uploadBinaryToProject,
  deleteProjectFile,
  extractPdfToMaterial,
  type ProjectFileDto,
  type ProjectFileType,
} from "./projects-api.js";
import { openProjectFileViewer, prettyTitle } from "./project-file-viewer.js";
import {
  buildPageHeader,
  confirmSheet,
  requestMenuClose } from "./sheet-ui.js";
import { clearPageHash, setPageHash } from "./page-routes.js";
import { zeichen } from "./zeichen.js";

const MATERIAL_PROJECT_NAME = "Eingespeistes Material";
const AI_PREFIX = "[QUELLE — DIALOG MIT EINER KI";

/** Faktentreue-Kennzeichnung für KI-Dialoge: nur die User-Aussagen zählen. */
const AI_HEADER =
  AI_PREFIX +
  ": Nur MEINE Aussagen sind meine Erfahrung und meine Themen. Die Antworten " +
  "der KI sind KEINE Fakten über mich — als Biografie-Quelle ignorieren.]\n\n";

type MaterialKind = "note" | "ai" | "transcript" | "reference";

const KIND_LABEL: Record<MaterialKind, string> = {
  note: "Eigene Notiz",
  ai: "Gespräch / KI-Dialog",
  transcript: "Mitschrift",
  reference: "Wissen / Dokument",
};

/** Eingabe-Art → gespeicherter project_file-Typ (Contract-Mapping). */
const KIND_TO_TYPE: Record<MaterialKind, ProjectFileType> = {
  note: "note",
  ai: "transcript",
  transcript: "transcript",
  reference: "reference",
};

let materialProjectId: string | null = null;
let sheetEl: HTMLElement | null = null;

/** Findet das Material-Projekt oder legt es einmalig an (gecacht). */
async function ensureMaterialProject(): Promise<string> {
  if (materialProjectId) return materialProjectId;
  const projects = await listProjects();
  const existing = projects.find((p) => p.name === MATERIAL_PROJECT_NAME);
  const id = existing
    ? existing.id
    : (
        await createProject({
          name: MATERIAL_PROJECT_NAME,
          description:
            "Texte, die ich ins Super-Gehirn einspeise (Notizen, Gespräche, Mitschriften).",
        })
      ).id;
  materialProjectId = id;
  return id;
}

/**
 * DRY-Komposition des finalen Datei-Inhalts (Contract). Wird vom Text-Pfad
 * direkt genutzt; der PDF-Pfad baut denselben Aufbau backend-seitig aus den
 * mitgeschickten Feldern (Beschreibung + Tag + extrahierter PDF-Text), darum
 * schickt der PDF-Pfad NUR die Rohfelder, nicht den fertigen Content.
 *
 * Aufbau: (Beschreibung ? +"\n\n") + (Tag ? "Schlagwörter: …\n\n") + Body.
 * Bei „Gespräch / KI-Dialog" wird zusätzlich der Faktentreue-Header
 * vorangestellt (nur die User-Aussagen zählen als Quelle).
 */
function composeContent(
  kind: MaterialKind,
  parts: { description?: string; tag?: string; body: string },
): string {
  const desc = parts.description?.trim() ?? "";
  const tag = parts.tag?.trim() ?? "";
  const body =
    (desc ? desc + "\n\n" : "") +
    (tag ? "Schlagwörter: " + tag + "\n\n" : "") +
    parts.body;
  return kind === "ai" ? AI_HEADER + body : body;
}

/** Lesbares Label für die Liste (gespeicherter Typ → Anzeige). */
function typeLabel(type: ProjectFileType): string {
  if (type === "note") return "Notiz";
  if (type === "reference") return "Wissen / Dokument";
  return "Mitschrift / Dialog";
}

// --- Deep-Rebuild, serialisiert (nie zwei gleichzeitig) -------------------
let regenRunning = false;
let regenPending = false;

async function runRegen(onStatus: (msg: string) => void): Promise<void> {
  if (regenRunning) {
    regenPending = true;
    onStatus(
      "Gespeichert. Wird beim laufenden Durchlauf des Super-Gehirns berücksichtigt …",
    );
    return;
  }
  regenRunning = true;
  onStatus(
    "Gespeichert. Wird vom Super-Gehirn verarbeitet … (kann ein paar Minuten dauern)",
  );
  try {
    const res = await fetch("/api/biography/regenerate", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ deep: true }),
    });
    onStatus(
      res.ok
        ? "Verarbeitet — deine Biografie kennt das jetzt."
        : "Gespeichert, aber die Verarbeitung meldete einen Fehler. Du kannst es später unter Mein Leben neu aufbauen.",
    );
  } catch {
    onStatus(
      "Gespeichert. Die Verarbeitung läuft evtl. noch — sonst später unter Mein Leben neu aufbauen.",
    );
  } finally {
    regenRunning = false;
    if (regenPending) {
      regenPending = false;
      void runRegen(onStatus);
    }
  }
}

// --- Sheet-Aufbau ----------------------------------------------------------
// Statisches Markup (keine interpolierten User-Daten → kein XSS-Risiko).
// Dynamische Inhalte (Liste) werden ausschließlich per textContent gesetzt.
const SHEET_HTML = `
  <div class="sheet-backdrop" data-sheet-close="1"></div>
  <div class="sheet-card material-card">
  <div class="page-body">
    <div class="material-form-modal" hidden>
      <div class="material-form-backdrop" data-form-close="1"></div>
      <div class="material-form-card">
        <div class="material-form-head">
          <strong>Neues Material</strong>
          <button type="button" class="close-btn material-form-x" data-form-close="1" aria-label="Schließen">${zeichen("schliessen", 18)}</button>
        </div>
    <p class="material-hint">
      Tippe Text ein oder hänge ein PDF an — Notizen, Gespräche, Mitschriften,
      Dokumente. Alles wird gespeichert und vom Super-Gehirn durchsuchbar
      gemacht und in deine Biografie und Themen verarbeitet.
    </p>
    <form class="material-form material-source" novalidate>
      <label class="material-label">
        Typ
        <select class="material-type" name="kind">
          <option value="note">Eigene Notiz</option>
          <option value="ai">Gespräch / KI-Dialog</option>
          <option value="transcript">Mitschrift</option>
          <option value="reference">Wissen / Dokument</option>
        </select>
      </label>
      <input
        class="material-title"
        type="text"
        name="title"
        placeholder="Titel (z. B. Gespräch mit Maya)"
        autocomplete="off"
      />
      <textarea
        class="material-text"
        name="text"
        placeholder="Text hier einfügen …"
      ></textarea>
      <input
        class="material-title material-tag"
        type="text"
        name="tag"
        placeholder="Schlagwort (optional, z. B. Gesundheit)"
        autocomplete="off"
      />
      <input
        class="material-title material-author"
        type="text"
        name="author"
        placeholder="Autor / Quelle (optional — z. B. Josef Krische; sonst erkennt es die KI)"
        autocomplete="off"
      />
      <div class="material-attach">
        <button type="button" class="material-drop">
          <span class="material-drop-icon" aria-hidden="true">${zeichen("dokument", 26)}</span>
          <span class="material-drop-text">PDF oder Audio (MP3) anhängen — ziehen oder klicken</span>
          <span class="material-drop-hint">PDF: Text wird ausgelesen · Audio: abspielbar gespeichert.</span>
        </button>
        <input
          class="material-file"
          type="file"
          accept="application/pdf,.pdf,audio/mpeg,audio/mp4,audio/x-m4a,audio/wav,audio/ogg,.mp3,.m4a,.wav,.ogg"
          hidden
        />
        <p class="material-attach-name" hidden></p>
      </div>
      <p class="material-ai-note" hidden>
        Gespräch / KI-Dialog: Nur <strong>deine</strong> Aussagen zählen als
        deine Erfahrung — die Antworten der KI werden als Quelle ignoriert.
      </p>
      <p class="material-ref-note" hidden>
        Wissen / Dokument: kommt ins Super-Gehirn (durchsuchbar), fließt aber
        <strong>nicht</strong> in „Mein Leben".
      </p>
      <button type="submit" class="btn btn--primary material-submit" disabled>
        Einspeisen &amp; verarbeiten
      </button>
    </form>
      </div>
    </div>
    <p class="material-status" role="status" aria-live="polite"></p>
    <h3 class="material-list-title">Eingespeist</h3>
    <div class="material-list"></div>
  </div>
  </div>
`;

function buildSheet(): HTMLElement {
  const el = document.createElement("div");
  // Pre.792 (#62): desktop-taugliches zentriertes Modal (ab 720px), mobil
  // unverändert Bottom-Sheet — generischer .sheet--modal-Modifier.
  el.className = "sheet material-sheet page-sheet";
  el.id = "material-sheet";
  el.setAttribute("role", "dialog");
  el.setAttribute("aria-modal", "true");
  el.hidden = true;
  el.innerHTML = SHEET_HTML;
  // Einheitlicher Kopf wie Mein Alltag (buildSheetHeader): Titel links + X
  // rechts, ganz oben in der Karte — bleibt beim Scrollen sichtbar (statt eines
  // mit-scrollenden Ecken-Buttons, der beim Scrollen verschwand).
  el.querySelector(".sheet-card")?.prepend(
    (() => {
      const openBtn = document.createElement("button");
      openBtn.type = "button";
      openBtn.className = "btn btn--primary btn--sm material-open-form";
      openBtn.textContent = "+ Einspeisen";
      return buildPageHeader("Material", closeMaterialSheet, {
        action: openBtn,
      });
    })(),
  );
  document.body.appendChild(el);
  /* 2026-08-30: Der eigene Aufruf ist entfallen — buildPageHeader meldet
     jede Seite mit geteiltem Kopf beim einen Wisch-Waechter an. Zwei
     Handler haetten denselben Rueckweg zweimal ausgeloest. */

  el.querySelectorAll("[data-sheet-close]").forEach((c) =>
    c.addEventListener("click", closeMaterialSheet),
  );
  // 2026-07-21 (Martin): Formular wohnt im Modal — die Seite gehoert der
  // Uebersicht. Oeffnen per Knopf, schliessen per X/Backdrop.
  const formModal = el.querySelector<HTMLElement>(".material-form-modal")!;
  el.querySelector(".material-open-form")?.addEventListener("click", () => {
    formModal.hidden = false;
  });
  el.querySelectorAll("[data-form-close]").forEach((c) =>
    c.addEventListener("click", () => {
      formModal.hidden = true;
    }),
  );

  const form = el.querySelector<HTMLFormElement>(".material-form")!;
  const fields = readFields(el);
  const status = el.querySelector<HTMLElement>(".material-status")!;

  const setStatus = (m: string, kind: "ok" | "error" = "ok"): void => {
    status.textContent = m;
    status.classList.toggle("material-status--error", kind === "error");
  };

  fields.type.addEventListener("change", () => {
    fields.aiNote.hidden = fields.type.value !== "ai";
    fields.refNote.hidden = fields.type.value !== "reference";
  });
  // Absenden braucht entweder Text ODER ein angehängtes PDF.
  fields.text.addEventListener("input", () => refreshSubmit(fields));

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    void submitMaterial({ fields, setStatus, el });
  });

  wireAttach(fields, setStatus);

  return el;
}

type SetStatus = (m: string, kind?: "ok" | "error") => void;

/**
 * Alle Eingabefelder eines Sheets gebündelt. `pendingPdf` trägt die aktuell
 * angehängte (noch nicht abgesendete) PDF-Datei.
 */
interface MaterialFields {
  type: HTMLSelectElement;
  title: HTMLInputElement;
  text: HTMLTextAreaElement;
  tag: HTMLInputElement;
  author: HTMLInputElement;
  drop: HTMLButtonElement;
  file: HTMLInputElement;
  attachName: HTMLElement;
  aiNote: HTMLElement;
  refNote: HTMLElement;
  submit: HTMLButtonElement;
  pendingPdf: File | null;
}

function readFields(el: HTMLElement): MaterialFields {
  return {
    type: el.querySelector<HTMLSelectElement>(".material-type")!,
    title: el.querySelector<HTMLInputElement>(".material-title")!,
    text: el.querySelector<HTMLTextAreaElement>(".material-text")!,
    tag: el.querySelector<HTMLInputElement>(".material-tag")!,
    author: el.querySelector<HTMLInputElement>(".material-author")!,
    drop: el.querySelector<HTMLButtonElement>(".material-drop")!,
    file: el.querySelector<HTMLInputElement>(".material-file")!,
    attachName: el.querySelector<HTMLElement>(".material-attach-name")!,
    aiNote: el.querySelector<HTMLElement>(".material-ai-note")!,
    refNote: el.querySelector<HTMLElement>(".material-ref-note")!,
    submit: el.querySelector<HTMLButtonElement>(".material-submit")!,
    pendingPdf: null,
  };
}

/** Absenden ist möglich, sobald Text vorhanden ist ODER ein PDF anhängt. */
function refreshSubmit(f: MaterialFields): void {
  f.submit.disabled = f.text.value.trim().length === 0 && f.pendingPdf === null;
}

/**
 * Verdrahtet das PDF-Anhängen (Klick öffnet Datei-Dialog, Drag&Drop ebenso).
 * Statt sofort hochzuladen wird die Datei nur vorgemerkt: Titel vorbefüllen
 * (editierbar), Typ auf „Wissen / Dokument", Text wird zur Beschreibung. Das
 * eigentliche Einspeisen passiert beim Absenden des einen Formulars.
 */
function wireAttach(f: MaterialFields, setStatus: SetStatus): void {
  const accept = (file: File): void => {
    const audio = isAudio(file);
    if (!isPdf(file) && !audio) {
      setStatus(
        "Bitte PDF oder Audio (MP3/M4A/WAV) wählen — andere Formate werden hier nicht gelesen.",
        "error",
      );
      return;
    }
    f.pendingPdf = file;
    // 2026-08-26: waren Emoji (Note / Blatt) direkt vor dem Dateinamen.
    // Gezeichnet, damit die Zeile eine Farbe traegt statt zweier.
    //
    // ⚠️ Der Dateiname bleibt ein TEXTKNOTEN. Er kommt aus einer Datei, die
    // jemand ausgewaehlt hat — in innerHTML gehoert er nicht, auch nicht
    // "nur kurz". Nur das Zeichen ist Markup.
    const artZeichen = document.createElement("span");
    artZeichen.className = "material-attach-zeichen";
    artZeichen.innerHTML = zeichen(audio ? "klang" : "dokument", 15);
    f.attachName.replaceChildren(artZeichen, ` ${file.name}`);
    f.attachName.hidden = false;
    f.drop.classList.add("material-drop--has-file");
    // Default-Typ „Wissen / Dokument"; Titel aus Dateiname vorbefüllen
    // (editierbar, nur wenn noch leer), Text-Feld wird zur Beschreibung.
    f.type.value = "reference";
    f.type.dispatchEvent(new Event("change"));
    if (!f.title.value.trim())
      f.title.value = file.name.replace(/\.(pdf|mp3|m4a|wav|ogg)$/i, "");
    f.text.placeholder = "Beschreibung (optional) …";
    refreshSubmit(f);
    setStatus("");
  };

  f.drop.addEventListener("click", () => f.file.click());
  f.file.addEventListener("change", () => {
    const file = f.file.files?.[0];
    f.file.value = ""; // gleiche Datei erneut wählbar
    if (file) accept(file);
  });

  f.drop.addEventListener("dragover", (e) => {
    e.preventDefault();
    f.drop.classList.add("material-drop--over");
  });
  const leave = (): void => f.drop.classList.remove("material-drop--over");
  f.drop.addEventListener("dragleave", leave);
  f.drop.addEventListener("drop", (e) => {
    e.preventDefault();
    leave();
    const file = e.dataTransfer?.files?.[0];
    if (file) accept(file);
  });
}

function isPdf(file: File): boolean {
  return file.type === "application/pdf" || /\.pdf$/i.test(file.name);
}

// 2026-07-14 (Martin): MP3s (z.B. KI-Musik) als Teil seiner Geschichte —
// abspielbar im Projekt gespeichert.
function isAudio(file: File): boolean {
  return (
    file.type.startsWith("audio/") || /\.(mp3|m4a|wav|ogg)$/i.test(file.name)
  );
}

/** Setzt das Formular nach erfolgreichem Einspeisen zurück. */
function resetForm(f: MaterialFields): void {
  f.text.value = "";
  f.title.value = "";
  f.tag.value = "";
  f.author.value = "";
  f.pendingPdf = null;
  f.attachName.textContent = "";
  f.attachName.hidden = true;
  f.drop.classList.remove("material-drop--has-file");
  f.text.placeholder = "Text hier einfügen …";
  refreshSubmit(f);
}

/**
 * Ein Absende-Pfad für Text UND PDF. Beim PDF wandern die Rohfelder zur
 * extract-pdf-Route (sie hängt die Beschreibung/den Tag vor den extrahierten
 * Text); beim Text komponiert `composeContent` den Inhalt lokal (DRY-Aufbau).
 */
async function submitMaterial(ctx: {
  fields: MaterialFields;
  setStatus: SetStatus;
  el: HTMLElement;
}): Promise<void> {
  const f = ctx.fields;
  const kind = f.type.value as MaterialKind;
  const fileType = KIND_TO_TYPE[kind];
  const body = f.text.value.trim();
  const tag = f.tag.value.trim();
  const author = f.author.value.trim();
  const pdf = f.pendingPdf;
  if (body.length === 0 && !pdf) return;

  const title =
    f.title.value.trim() ||
    (pdf ? pdf.name.replace(/\.pdf$/i, "") : "") ||
    `${KIND_LABEL[kind]} vom ${new Date().toLocaleDateString("de-AT")}`;

  f.submit.disabled = true;
  ctx.setStatus(
    pdf ? (isAudio(pdf) ? "Audio wird hochgeladen …" : "PDF wird gelesen …") : "Speichere …",
  );
  try {
    const projectId = await ensureMaterialProject();
    let savedTitle = title;
    if (pdf && isAudio(pdf)) {
      // 2026-07-14 (Martin): MP3/Audio -> abspielbare Projekt-Datei
      // (Server inferType: recording). Beschreibung landet im Titelfeld-
      // Kontext, nicht extrahierbar wie PDF-Text.
      const item = await uploadBinaryToProject(projectId, pdf, title);
      savedTitle = item.title;
    } else if (pdf) {
      // Beschreibung/Tag gehen als Rohfelder mit; Backend baut den Content
      // (Beschreibung + Schlagwörter + extrahierter PDF-Text) zusammen.
      const item = await extractPdfToMaterial(projectId, pdf, {
        title,
        description: body || undefined,
        fileType,
        tag: tag || undefined,
        author: author || undefined,
      });
      savedTitle = item.title;
    } else {
      // Text-Pfad: Content lokal komponieren (DRY) und ablegen.
      const content = composeContent(kind, { tag, body });
      await createProjectFile(projectId, {
        title,
        content,
        type: fileType,
        author: author || undefined,
      });
    }
    resetForm(f);
    await renderList(ctx.el);
    // Modal zu — der neue Eintrag steht sichtbar oben in der Liste.
    ctx.el
      .querySelector<HTMLElement>(".material-form-modal")
      ?.setAttribute("hidden", "");
    // Pre.964: Der Erfolg IST hier passiert — die Datei liegt durchsuchbar im
    // Super-Gehirn (Embedding macht die Route). Der (langsame, optionale)
    // Biografie-Neuaufbau läuft STILL im Hintergrund und darf den Erfolgs-
    // Status NICHT überschreiben.
    // 2026-08-26 (Norm A): das ✓ war ein Dingbat im Fliesstext.
    ctx.setStatus(`„${savedTitle}" ist im Super-Gehirn — durchsuchbar`);
    void runRegen(() => {});
  } catch (err) {
    ctx.setStatus(`Konnte nicht speichern: ${(err as Error).message}`, "error");
    refreshSubmit(f);
  }
}

// Aufgeklappte Tage (wie Mein Alltag — übersteht Neu-Aufbauten).
const materialOpenDays = new Set<string>();
let materialOpenInit = false;

function materialEmpty(text: string): HTMLElement {
  const p = document.createElement("p");
  p.className = "material-empty";
  p.textContent = text;
  return p;
}

/** "2026-06-20" → "Fr, 20. Juni" (gleiches Format wie Mein Alltag). */
function formatMaterialDay(dayKey: string): string {
  try {
    return new Date(dayKey + "T12:00:00").toLocaleDateString("de-AT", {
      weekday: "short",
      month: "long",
      day: "numeric",
    });
  } catch {
    return dayKey;
  }
}

async function renderList(el: HTMLElement): Promise<void> {
  const list = el.querySelector<HTMLElement>(".material-list")!;
  let files: ProjectFileDto[] = [];
  try {
    const projectId = await ensureMaterialProject();
    files = await listProjectFiles(projectId);
  } catch {
    list.replaceChildren(materialEmpty("Konnte die Liste nicht laden."));
    return;
  }
  if (files.length === 0) {
    list.replaceChildren(materialEmpty("Noch nichts eingespeist."));
    return;
  }
  files.sort((a, b) => b.createdAt.localeCompare(a.createdAt));

  // Nach Tag gruppieren (wie Mein Alltag): Tages-Akkordeon + Zeit-Spur.
  const byDay = new Map<string, ProjectFileDto[]>();
  for (const f of files) {
    const day = (f.createdAt || "").slice(0, 10) || "—";
    if (!byDay.has(day)) byDay.set(day, []);
    byDay.get(day)!.push(f);
  }
  const days = [...byDay.keys()].sort((a, b) => b.localeCompare(a));
  if (!materialOpenInit) {
    materialOpenInit = true;
    if (days[0]) materialOpenDays.add(days[0]);
  }

  const frag = document.createDocumentFragment();
  for (const day of days) {
    frag.appendChild(buildMaterialDay(day, byDay.get(day)!, el));
  }
  list.replaceChildren(frag);
}

/** Ein Tag als aufklappbares Akkordeon mit Zeit-Spur (Mein-Alltag-Klassen). */
function buildMaterialDay(
  day: string,
  items: ProjectFileDto[],
  el: HTMLElement,
): HTMLElement {
  const block = document.createElement("details");
  block.className = "alltag-day";
  block.open = materialOpenDays.has(day);
  block.addEventListener("toggle", () => {
    if (block.open) materialOpenDays.add(day);
    else materialOpenDays.delete(day);
  });

  const summary = document.createElement("summary");
  summary.className = "alltag-day-label";
  const label = document.createElement("span");
  label.className = "alltag-day-label-text";
  label.textContent = day === "—" ? "Ohne Datum" : formatMaterialDay(day);
  summary.appendChild(label);
  const count = document.createElement("span");
  count.className = "alltag-day-count";
  count.textContent =
    items.length === 1 ? "1 Eintrag" : `${items.length} Einträge`;
  summary.appendChild(count);
  block.appendChild(summary);

  const body = document.createElement("div");
  body.className = "alltag-day-body";
  const timeline = document.createElement("div");
  timeline.className = "alltag-timeline";
  const group = document.createElement("div");
  group.className = "alltag-tl-group";
  for (const f of items) {
    const tlItem = document.createElement("div");
    tlItem.className = "alltag-tl-item";
    tlItem.appendChild(buildRow(f, el));
    group.appendChild(tlItem);
  }
  timeline.appendChild(group);
  body.appendChild(timeline);
  block.appendChild(body);
  return block;
}

function buildRow(file: ProjectFileDto, el: HTMLElement): HTMLElement {
  // Timeline-Knoten im Mein-Alltag-Format (Klassen geteilt).
  const li = document.createElement("div");
  li.className = "material-item alltag-entry";

  // Kopfzeile: Uhrzeit (Knoten-Label) + Titel. Datum steht im Tages-Header.
  const time = (file.createdAt || "").slice(11, 16);
  const head = document.createElement("div");
  head.className = "material-item-head";
  const timeEl = document.createElement("span");
  timeEl.className = "alltag-entry-time";
  timeEl.textContent = time || "Material";
  head.appendChild(timeEl);
  // Pre.794 (#36): Titel klickt in den GETEILTEN grossen Viewer (Projekt-
  // Datei-Ansicht: Bearbeiten, Loeschen, Voll-Inhalt) statt 200px-Guckloch.
  const t = document.createElement("button");
  t.type = "button";
  t.className = "project-file-title";
  t.textContent = prettyTitle(file.title);
  t.addEventListener("click", () => void openViewer(file, el));
  const meta = document.createElement("span");
  meta.className = "material-item-meta";
  meta.textContent =
    typeLabel(file.type) + (file.author ? ` · von ${file.author}` : "");
  head.appendChild(t);

  const actions = document.createElement("div");
  actions.className = "material-item-actions";
  // Pre.974: „Vorlesen" für text-tragende Materialien (Notiz/Mitschrift/Wissen/
  // Kapitel/Vokabeln) — öffnet den Viewer, in dem der geteilte Vorlese-Player
  // sitzt (kurze Notiz = einfaches Vorlesen, ein Buch = Kapitel-Hörbuch).
  const PLAYABLE: ReadonlySet<ProjectFileType> = new Set([
    "note",
    "transcript",
    "reference",
    "chapter",
    "vocabulary",
  ]);
  if (PLAYABLE.has(file.type)) {
    const play = document.createElement("button");
    play.type = "button";
    play.className = "btn btn--primary btn--sm material-item-play";
    // 2026-08-26 (Norm A): war "▶ Vorlesen" mit dem Dingbat U+25B6.
    play.innerHTML = `${zeichen("abspielen", 14)}<span>Vorlesen</span>`;
    play.addEventListener("click", () => void openViewer(file, el));
    actions.append(play);
  }
  const del = document.createElement("button");
  del.type = "button";
  del.className = "btn btn--ghost btn--sm material-item-del";
  del.textContent = "Löschen";
  actions.append(del);

  del.addEventListener("click", () => void removeFile(file, el));

  li.append(head, meta, actions);
  return li;
}

async function openViewer(
  file: ProjectFileDto,
  el: HTMLElement,
): Promise<void> {
  const projectId = await ensureMaterialProject();
  await openProjectFileViewer(projectId, file.id, {
    onChange: () => void renderList(el),
  });
}

async function removeFile(
  file: ProjectFileDto,
  el: HTMLElement,
): Promise<void> {
  const ok = await confirmSheet({
    title: `„${file.title}" löschen?`,
    confirmLabel: "Löschen",
    danger: true,
  });
  if (!ok) return;
  try {
    const projectId = await ensureMaterialProject();
    await deleteProjectFile(projectId, file.id);
    // Neu aufbauen (entfernt auch den leeren Timeline-Knoten/Tag sauber).
    void renderList(el);
  } catch (err) {
    window.alert(`Löschen fehlgeschlagen: ${(err as Error).message}`);
  }
}

// --- Öffentliche API -------------------------------------------------------

export function openMaterialSheet(): void {
  window.dispatchEvent(new CustomEvent("voice-v8:page-open"));
  if (!sheetEl) sheetEl = buildSheet();
  // Pre.792 (#36): Nav-Drawer schließen (Bestandslücke — blieb mobil unter
  // dem Sheet offen; gleiches Muster wie invoices).
  requestMenuClose();
  sheetEl.hidden = false;
  void renderList(sheetEl);
  // Pre.792 (#36): reload-fest + teilbar (Muster invoices).
  setPageHash("material");
}

export function closeMaterialSheet(): void {
  if (sheetEl) sheetEl.hidden = true;
  clearPageHash("material");
}
